const MONTH_LABELS = [
	"Jan",
	"Feb",
	"Mar",
	"Apr",
	"May",
	"Jun",
	"Jul",
	"Aug",
	"Sep",
	"Oct",
	"Nov",
	"Dec",
] as const;

type MonthLabel = (typeof MONTH_LABELS)[number];

const MONTH_TO_INDEX = new Map(MONTH_LABELS.map((label, index) => [label, index]));
const MONTH_LABEL_PATTERN = MONTH_LABELS.join("|");
const PERIOD_PATTERN = new RegExp(
	`^(${MONTH_LABEL_PATTERN})\\s(\\d{4})\\s-\\s((${MONTH_LABEL_PATTERN})\\s(\\d{4})|Present)$`,
);

export type ExperiencePeriodParts = {
	startMonth: string;
	endMonth: string;
	isCurrent: boolean;
};

function parseMonthInput(value: string) {
	const match = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(value);
	if (!match) return null;
	const year = Number(match[1]);
	const monthIndex = Number(match[2]) - 1;
	return { year, monthIndex };
}

function toMonthInput(year: number, monthIndex: number) {
	return `${year.toString().padStart(4, "0")}-${String(monthIndex + 1).padStart(2, "0")}`;
}

function toComparableMonth(year: number, monthIndex: number) {
	return year * 12 + monthIndex;
}

export function monthInputToLabel(value: string) {
	const parsed = parseMonthInput(value);
	if (!parsed) return null;
	return `${MONTH_LABELS[parsed.monthIndex]} ${parsed.year}`;
}

export function buildExperiencePeriod(
	startMonth: string,
	endMonth: string,
	isCurrent: boolean,
) {
	const startLabel = monthInputToLabel(startMonth);
	if (!startLabel) {
		return { ok: false as const, error: "Bulan mulai tidak valid." };
	}

	if (isCurrent) {
		return { ok: true as const, period: `${startLabel} - Present` };
	}

	const endLabel = monthInputToLabel(endMonth);
	if (!endLabel) {
		return { ok: false as const, error: "Bulan selesai tidak valid." };
	}

	const startParsed = parseMonthInput(startMonth);
	const endParsed = parseMonthInput(endMonth);
	if (!startParsed || !endParsed) {
		return { ok: false as const, error: "Format bulan tidak valid." };
	}

	const startValue = toComparableMonth(startParsed.year, startParsed.monthIndex);
	const endValue = toComparableMonth(endParsed.year, endParsed.monthIndex);
	if (endValue < startValue) {
		return {
			ok: false as const,
			error: "Bulan selesai harus sama atau setelah bulan mulai.",
		};
	}

	return { ok: true as const, period: `${startLabel} - ${endLabel}` };
}

export function parseExperiencePeriod(period: string): ExperiencePeriodParts | null {
	const trimmed = period.trim();
	const match = PERIOD_PATTERN.exec(trimmed);
	if (match) {
		const startMonthIndex = MONTH_TO_INDEX.get(match[1] as MonthLabel);
		const startYear = Number(match[2]);
		if (startMonthIndex === undefined) return null;

		const startMonth = toMonthInput(startYear, startMonthIndex);

		if (match[3] === "Present") {
			return {
				startMonth,
				endMonth: "",
				isCurrent: true,
			};
		}

		const endMonthIndex = MONTH_TO_INDEX.get(match[4] as MonthLabel);
		const endYear = Number(match[5]);
		if (endMonthIndex === undefined) return null;

		return {
			startMonth,
			endMonth: toMonthInput(endYear, endMonthIndex),
			isCurrent: false,
		};
	}

	const yearRange = /^(\d{4})\s*-\s*(present|\d{4})$/i.exec(trimmed);
	if (yearRange) {
		const startYear = Number(yearRange[1]);
		const startMonth = `${startYear}-01`;
		if (yearRange[2].toLowerCase() === "present") {
			return {
				startMonth,
				endMonth: "",
				isCurrent: true,
			};
		}

		return {
			startMonth,
			endMonth: `${yearRange[2]}-12`,
			isCurrent: false,
		};
	}

	const singleYear = /^(\d{4})$/.exec(trimmed);
	if (singleYear) {
		return {
			startMonth: `${singleYear[1]}-01`,
			endMonth: `${singleYear[1]}-12`,
			isCurrent: false,
		};
	}

	return null;
}

export function validateExperiencePeriod(period: string) {
	const parsed = parseExperiencePeriod(period);
	if (!parsed) {
		return {
			ok: false as const,
			error: "period harus berformat seperti 'Jan 2024 - Present' atau 'Jan 2024 - Mar 2025'.",
		};
	}

	const normalized = buildExperiencePeriod(
		parsed.startMonth,
		parsed.endMonth,
		parsed.isCurrent,
	);

	if (!normalized.ok) {
		return { ok: false as const, error: normalized.error };
	}

	return { ok: true as const, period: normalized.period };
}
